import { ChangeEvent, FC, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  useMediaQuery,
} from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { FormikHelpers, useFormik } from "formik";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import * as Yup from "yup";

import { Button, PhoneInput, Switch } from "components";
import { useCountries } from "services/app";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import theme from "theme";

import { FormValidation } from ".";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    margin: 0,
  },
  smallText: {
    fontSize: "0.8rem",
  },
  textInputRoot: {
    textAlign: "left",
    "& label.Mui-focused": {
      color: theme.custom.palette.purple.main,
    },
    "&:hover label": {
      color: theme.custom.palette.purple.main,
    },
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: theme.custom.palette.purple.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.custom.palette.purple.main,
      },
    },
  },
  spacingTop: {
    paddingTop: theme.spacing(4),
  },
}));

interface FormValues {
  first_name: string;
  last_name: string;
  country_code: CountryCode;
  phone: string;
  account_type?: string;
  sms_notifications: boolean;
  email_notifications: boolean;
  buyer: boolean;
}

interface FormProps {
  onSubmit: (values: FormValues) => Promise<void>;
  initialValues: FormValues;
}

const formSchema = Yup.object().shape({
  first_name: Yup.string().required().label("First name").max(30),
  last_name: Yup.string().required().label("Last name").max(150),
  country_code: Yup.string().required().label("Country"),
  phone: Yup.string()
    .required()
    .label("Phone number")
    .test("is-valid-phone", "Phone number is not valid", function (value) {
      const { country_code } = this.parent;
      if (!value) {
        return true;
      }
      return isValidPhoneNumber(value, country_code);
    }),
});

const Form: FC<FormProps> = ({ initialValues, onSubmit }) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { data: countries = [] } = useCountries();
  const [snackbarConfig, setSnackbarConfig] = useState<FormValidation>({
    open: false,
  });

  const handleSubmit = async (
    formValues: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    try {
      setSubmitting(true);
      const buyer = formValues.account_type === "buyer" ? true : false;
      await onSubmit({
        ...formValues,
        phone: formValues.phone.replace(/\D/g, ""),
        buyer,
      });
      setSnackbarConfig({
        open: true,
        status: "success",
        message: "Profile updated",
      });
    } catch (e) {
      setSnackbarConfig({
        open: true,
        status: "error",
        message: DEFAULT_ERROR_MESSAGE,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<FormValues>({
    onSubmit: handleSubmit,
    validationSchema: formSchema,
    initialValues: {
      ...initialValues,
      account_type: initialValues.buyer === true ? "buyer" : "seller",
    },
  });

  const handleCountryChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const countryCode = event.target.value;
    await formik.setFieldValue("country_code", countryCode);

    if (formik.values.phone) {
      formik.setFieldTouched("phone", true, true);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} className={classes.spacingTop}>
      <Snackbar
        open={snackbarConfig.open}
        autoHideDuration={3000}
        onClose={() => setSnackbarConfig({ open: false })}
      >
        <Alert
          onClose={() => setSnackbarConfig({ open: false })}
          severity={snackbarConfig.status}
        >
          {snackbarConfig.message}
        </Alert>
      </Snackbar>
      <Grid container spacing={3}>
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              id="first_name"
              type="first_name"
              name="first_name"
              label="First name"
              value={formik.values.first_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.first_name && !!formik.errors.first_name}
              helperText={formik.touched.first_name && formik.errors.first_name}
              disabled={formik.isSubmitting}
              className={classes.textInputRoot}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              id="last_name"
              type="last_name"
              name="last_name"
              label="Last name"
              value={formik.values.last_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.last_name && !!formik.errors.last_name}
              helperText={formik.touched.last_name && formik.errors.last_name}
              disabled={formik.isSubmitting}
              className={classes.textInputRoot}
            />
          </Grid>
        </Grid>
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              select
              id="country_code"
              type="country_code"
              name="country_code"
              label="Country"
              onChange={handleCountryChange}
              onBlur={formik.handleBlur}
              value={formik.values.country_code}
              error={
                formik.touched.country_code && !!formik.errors.country_code
              }
              helperText={
                formik.touched.country_code && formik.errors.country_code
              }
              disabled={formik.isSubmitting}
              className={classes.textInputRoot}
            >
              {countries.map(country => (
                <MenuItem value={country.code} key={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneInput
              value={formik.values.phone}
              countryCode={formik.values.country_code}
              onChange={value => formik.setFieldValue("phone", value)}
              onBlur={() => formik.setFieldTouched("phone", true)}
              error={formik.touched.phone && !!formik.errors.phone}
              helperText={formik.touched.phone && formik.errors.phone}
              disabled={formik.isSubmitting}
            />
          </Grid>
        </Grid>
      </Grid>
      <Box mt={5} textAlign="left">
        <Typography variant="h6">Account Type</Typography>
        <FormControl component="fieldset">
          <RadioGroup aria-label="account_type">
            <Box mb={2}>
              <FormControlLabel
                name="account_type"
                value="seller"
                checked={formik.values.account_type === "seller"}
                control={
                  <Radio
                    style={{
                      color: formik.isSubmitting
                        ? theme.custom.palette.purple.light
                        : theme.custom.palette.purple.main,
                    }}
                  />
                }
                label={
                  <>
                    <Typography>Seller Account</Typography>
                    <Typography className={classes.smallText}>
                      I&apos;m interested in receiving offers from royalties I
                      own
                    </Typography>
                  </>
                }
                onChange={formik.handleChange}
                disabled={formik.isSubmitting}
              />
            </Box>
            <Box>
              <FormControlLabel
                name="account_type"
                value="buyer"
                checked={formik.values.account_type === "buyer"}
                control={
                  <Radio
                    style={{
                      color: formik.isSubmitting
                        ? theme.custom.palette.purple.light
                        : theme.custom.palette.purple.main,
                    }}
                  />
                }
                label={
                  <>
                    <Typography>Investor Account</Typography>
                    <Typography className={classes.smallText}>
                      I&apos;m interested in purchasing royalty assets
                    </Typography>
                  </>
                }
                onChange={formik.handleChange}
                disabled={formik.isSubmitting}
              />
            </Box>
          </RadioGroup>
        </FormControl>
      </Box>
      <Box mt={3} mb={1} textAlign="left">
        <Typography variant="h6">Communication Preferences</Typography>
        <FormGroup>
          <Box maxWidth={600}>
            <Switch
              name="sms_notifications"
              onChange={formik.handleChange}
              disabled={formik.isSubmitting}
              checked={formik.values.sms_notifications == true}
              label="Send me text messages about new activity on my listings."
              subLabel="By
            enabling this option, you agree to receive text messages from
            Royalty Exchange at the number provided. Message and data rates
            may apply. Message frequency varies."
            />
            <Switch
              name="email_notifications"
              checked={formik.values.email_notifications == true}
              onChange={formik.handleChange}
              disabled={formik.isSubmitting}
              label="Send me emails about new listings on The Exchange."
            />
          </Box>
        </FormGroup>
      </Box>
      <Box display="flex" justifyContent="end" mt={isMobile ? 3 : 0}>
        <Button
          size="large"
          variant="contained"
          disabled={formik.isSubmitting || !formik.isValid}
          type="submit"
          isLoading={formik.isSubmitting}
          style={{ width: isMobile ? "100%" : "179px" }}
        >
          {formik.isSubmitting ? "Submitting" : "Update"}
        </Button>
      </Box>
    </form>
  );
};

export default Form;
